import React, { useEffect, useMemo, useState, useCallback, useRef, FC } from 'react';
import { useHistory } from 'react-router';
import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import moment from 'moment';
import _ from 'lodash';
import clsx from 'clsx';

import FixedLoader from '../../../components/FixedLoader/FixedLoader';
import Api from '../../../api';
import { RateBenchmarkResponse } from '../../../api/Api';
import useActionApi from '../../../hooks/useActionApi';
import { usePersistedState } from '../../../hooks';
import { useLanguageCheck } from '../../../hooks/useLanguageCheck';
import entities from '../../../reduxStore/entityKeys';
import { HygienistProps, JobProps, MembershipStatusResponse, PartialOffer } from 'utils/types';
import { MINIMUM_PARTIAL_SHIFT_HOURS, JOB_STATUSES, PARTIAL_OFFER_STATUS, COUNTRY } from 'utils/constants';
import { stringToDate } from 'utils/time';
import { getTalentParkingLabel } from 'utils/parking';
import { formatDowntimeLabel } from 'utils/downtime';
import { buildRateBenchmarkRequestParamsFromJob, getTalentMarketBenchmarkRate } from '../../../utils/rateGuidance';
import { calculateRateRange } from '../../../utils/rateCalculations';
import {
    canRequestMaxBookedIncrease,
    canUseInstabookByMembership,
    getJobRequesterMembershipDecision,
    getMembershipRestrictionDialogCopy,
    getRateCalculationTalent,
} from '../../../utils/membership';
import { requestHygienistCalendarRefresh } from '../HygienistHomePage/hooks/useHygienistCalendarData';

// Components
import Popup from 'components/Popup';
import MaterialInput from 'components/Inputs/MaterialInput';
import LanguageSelectionModal from '../../../components/LanguageSelectionModal';
import ConfirmDialog from '../../../components/ConfirmDialog';
import { MembershipRestrictionModal, type MembershipDialogCopy } from '../../../components/Modal';
import { ShiftPopup } from '../ShiftDetailPage/components';
import { AdminTasksDisplay } from './components/AdminTasksDisplay';
import MaxBookedPopup from './components/MaxBookedPopup';
import OfferModal from './components/OfferModal';
import ShiftDescriptionContainer from './components/ShiftDescriptionContainer';
import InstaBookOfferModal from './components/InstaBookOffer';
import { convertDentalOfficeStatus } from '../ShiftDetailPage/components';
import { getPaymentTerms, getPaymentMethod, getPaymentType } from '../../../utils';

// Styled components
import { ContainedButton, OutlinedButton } from './AvailableJob.styles';
import componentStyles from './components/components.module.scss';

// Icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import TimeToLeaveOutlinedIcon from '@mui/icons-material/TimeToLeaveOutlined';
import DiningOutlinedIcon from '@mui/icons-material/DiningOutlined';
import DesktopMacOutlinedIcon from '@mui/icons-material/DesktopMacOutlined';
import TimelapseOutlinedIcon from '@mui/icons-material/TimelapseOutlined';
import WavesOutlinedIcon from '@mui/icons-material/WavesOutlined';
import BlurLinearOutlinedIcon from '@mui/icons-material/BlurLinearOutlined';
import LineAxisOutlinedIcon from '@mui/icons-material/LineAxisOutlined';
import SpaOutlinedIcon from '@mui/icons-material/SpaOutlined';
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import SentimentSatisfiedAltOutlinedIcon from '@mui/icons-material/SentimentSatisfiedAltOutlined';
import LaptopMacOutlinedIcon from '@mui/icons-material/LaptopMacOutlined';
import heartFilled from '../../../images/heartFilled.svg';
import heartUnfilled from '../../../images/heartUnfilled.svg';
import instabook from 'images/job-option/instabook.png';

import TalentNavbar from '../../../components/TalentNavbar';
import TalentPageTitle from '../../../components/TalentNavbar/TalentPageTitle';
import styles from './AvailableJobPage.module.scss';
import { useOfficeFavourite } from './useOfficeFavourite';

dayjs.extend(isToday);

// @ts-ignore
const TempStars = window.TempStars;

const PARTIAL_OFFER_TIME_WINDOW = 72;

// Re-export for backwards compatibility (other files import these from here)
export { JOB_STATUSES, PARTIAL_OFFER_STATUS };

// ============ HELPER COMPONENTS ============

interface UpdateCDHOCertificateProps {
    hygienist: HygienistProps;
    onClose: () => void;
}

const UpdateCDHOCertificate: FC<UpdateCDHOCertificateProps> = ({ hygienist, onClose }) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [CDHONumber, setCDHONumber] = useState<string>('');

    const onUpdateCDHOCertificate = async () => {
        setLoading(true);
        try {
            await Api.updateHygienistAccount({ CDHONumber });
            hygienist.CDHONumber = Number(CDHONumber);
        } catch (error) {
            console.error('Error updating hygienist account:', error);
            throw error;
        } finally {
            setLoading(false);
            onClose();
        }
    };

    return (
        <>
            <div className={componentStyles.popupContainer}>
                <div className={componentStyles.titleContainer}>
                    <h2 className={componentStyles.popupTitle}>Please Update Your Certificate Number</h2>
                    <p className={componentStyles.popupSubtitle}>READ CAREFULLY</p>
                </div>
                <p className={componentStyles.modalDescription}>
                    With the recent changes to the requirements for Level 2 Dental Assistants, we now require your NDABE certificate number to be on your profile before you can submit an offer.
                </p>
                <MaterialInput
                    className={componentStyles.inputContainer}
                    label="Certificate Number"
                    value={CDHONumber}
                    onChange={(e: any) => setCDHONumber(e.target.value)}
                    name="CDHONumber"
                    helperText={CDHONumber.length >= 4 ? '' : 'Please enter your Certificate Number'}
                    error={CDHONumber.length < 4}
                />
                <ContainedButton
                    className={clsx(componentStyles.modalButton, 'no-fastclick')}
                    variant="contained"
                    size="large"
                    onClick={onUpdateCDHOCertificate}
                >
                    Done
                </ContainedButton>
                <OutlinedButton
                    className={clsx(componentStyles.modalButton, 'no-fastclick')}
                    variant="outlined"
                    size="large"
                    onClick={onClose}
                >
                    Cancel
                </OutlinedButton>
            </div>
            <FixedLoader loading={loading} />
        </>
    );
};

interface OfferSubmitData {
    startTime: string;
    endTime: string;
    hourlyRate: number;
    isPartial: boolean;
    showJobSeekingStatus?: boolean;
}

export function canShowAvailableJobSendOffer({
    hasValidShifts,
    job,
    hygienistId,
    hygienistOffers,
    isTalentQualifiedForInstabook = false,
}: {
    hasValidShifts: boolean;
    job: JobProps;
    hygienistId?: number | string;
    hygienistOffers: PartialOffer[];
    isTalentQualifiedForInstabook?: boolean;
}) {
    if (!hasValidShifts || !job?.id) return false;
    // T2-6567 (Problem B): only hide Send Offer for an Instabook *candidate* when the
    // office posted the shift with Instabook AND the talent actually qualifies to
    // Instabook it (isTalentQualifiedForInstabook) — in that case the Instabook CTA
    // renders instead. On a non-Instabook shift, or when the talent can't actually
    // Instabook (e.g. below Pro/Elite tier), Send Offer must still show, otherwise
    // there is no action button at all.
    if ((job as any).isInstabookCandidate && (job as any).Instabook && isTalentQualifiedForInstabook) return false;
    const myId = Number(hygienistId);
    const jobHyg = Number(job.hygienistId ?? 0);
    if (jobHyg > 0) return false;
    if (myId) {
        const mineForJob = hygienistOffers.filter(
            o => o.jobId === job.id && Number(o.hygienistId) === myId
        );
        const hasActiveOffer = mineForJob.some(
            o =>
                o.status === PARTIAL_OFFER_STATUS.POSTED ||
                o.status === PARTIAL_OFFER_STATUS.ACCEPTED
        );
        if (hasActiveOffer) return false;
    }
    return true;
}

export function getOfferSubmitErrorState(err: any) {
    const membershipDialog = getMembershipRestrictionDialogCopy(err);
    if (membershipDialog) return { membershipDialog, workExperienceRequired: false };

    const msg = typeof err?.message === 'string' ? err.message : String(err ?? '');
    return {
        membershipDialog: null,
        workExperienceRequired: msg.includes('Work experience is required'),
    };
}

export async function runPostOfferFlow({
    officeScopeLocationId,
    goToOfficeScopedList,
    checkOtherShiftsForOffice,
    showOtherShiftsPrompt,
    goHome,
}: {
    officeScopeLocationId?: number | string;
    goToOfficeScopedList: () => void;
    checkOtherShiftsForOffice: () => Promise<boolean>;
    showOtherShiftsPrompt: () => void;
    goHome: () => void;
}) {
    if (officeScopeLocationId) {
        goToOfficeScopedList();
        return;
    }

    const hasOtherShifts = await checkOtherShiftsForOffice();
    if (hasOtherShifts) {
        showOtherShiftsPrompt();
        return;
    }

    goHome();
}

interface OfferConfirmationProps {
    job: JobProps;
    offerData: OfferSubmitData;
    hygienist: HygienistProps;
    onConfirm: () => void;
}

const OfferConfirmation: FC<OfferConfirmationProps> = ({ job, offerData, hygienist, onConfirm }) => {
    const from = job.startDate?.replace(/-/g, '/') + ' ' + offerData.startTime;
    const to = job.startDate?.replace(/-/g, '/') + ' ' + offerData.endTime;
    const scentPolicy = job.location?.scentFreePolicy;

    const scentPolicyMessage = (() => {
        if (scentPolicy === 'not-scent-free' || !scentPolicy) {
            return 'Note: This office has indicated they do not provide a scent-free work environment.';
        }
        return null;
    })();

    const getJobSeekingStatusText = () => {
        switch (hygienist?.showForHire) {
            case 0: return 'Temping only';
            case 1: return 'Actively looking for a position';
            case 2: return 'Open to great opportunities';
            default: return null;
        }
    };

    const jobSeekingStatusText = getJobSeekingStatusText();

    return (
        <div className={componentStyles.popupContainer}>
            <div className={componentStyles.titleContainer}>
                <h2 className={componentStyles.popupTitle__confirm}>Nicely Done! Your Offer is Sent.</h2>
                <p className={componentStyles.popupSubtitle}>Important! Read Carefully</p>
            </div>
            <div className={componentStyles.contentContainer}>
                <div className={componentStyles.modalDescription}>
                    <span className={componentStyles.emphasizedText}>
                        We've notified {job.location?.practiceName} of your offer to work their shift.
                    </span>
                </div>
                <div className={componentStyles.modalDescription}>They will have 18hrs to accept your Offer.</div>
                <div className={componentStyles.modalDescription}>
                    <span className={componentStyles.emphasizedText}>Keep a close eye on your email inbox and phone.</span>
                </div>
                <div className={componentStyles.modalDescription}>
                    If your offer is accepted, we'll notify you immediately by email and text message.
                </div>
                {scentPolicyMessage && (
                    <div className={componentStyles.modalDescription} style={{ color: '#FB7672', fontWeight: 600 }}>
                        {scentPolicyMessage}
                    </div>
                )}
                <div className={componentStyles.shiftDescriptionWrapper}>
                    <ShiftDescriptionContainer
                        header={job.location?.practiceName || ''}
                        address={job.location?.address || ''}
                        from={from}
                        to={to}
                        rate={offerData.hourlyRate}
                        isConfirmModal={true}
                    />
                </div>
                {jobSeekingStatusText && offerData.showJobSeekingStatus && (
                    <div className={componentStyles.jobSeekingStatusConfirm}>
                        <div>Job-Seeking Status: <span className={componentStyles.statusText}>{jobSeekingStatusText}</span></div>
                        <div className={componentStyles.statusVisibility}>This will be visible to {job.location?.practiceName}</div>
                    </div>
                )}
            </div>
            <ContainedButton
                className={clsx(componentStyles.modalButton, 'no-fastclick')}
                variant="contained"
                size="large"
                onClick={onConfirm}
            >
                Agreed & Committed
            </ContainedButton>
        </div>
    );
};

// ============ MAIN COMPONENT ============

const AvailableJobPage: React.FC = () => {
    const history = useHistory();
    
    // ===== PAGE STATE =====
    const [trackData, setTrackData] = useState<any>(null);
    const [hireJobsCount, setHireJobsCount] = useState<number>(0);
    const [isFavouriteDentist, setFavouriteDentist] = useState<boolean | null>(null); // null = not yet initialized
    const [checkFavouriteLocationLoading, setCheckFavouriteLocationLoading] = useState(false);
    const [eligibilityRefresh, setEligibilityRefresh] = useState(0);

    // ===== OFFER/MODAL STATE (from MakeOffer) =====
    const [isOfferModalOpen, setIsOfferModalOpen] = useState(false);
    const [offerType, setOfferType] = useState<'full' | 'partial'>('full');
    const [isOpenHideOfferModal, setIsOpenHideOfferModal] = useState<boolean>(false);
    const [isOpenCDHOCertificateModal, setIsOpenCDHOCertificateModal] = useState<boolean>(false);
    const [isMaxBookedPopupOpen, setIsMaxBookedPopupOpen] = useState<boolean>(false);
    const [completedShifts, setCompletedShifts] = useState<number>(0);
    const [showJobSeekingStatus, setShowJobSeekingStatus] = useState<boolean>(true);
    const [isLoadingHygienist, setIsLoadingHygienist] = useState<boolean>(false);
    const [isMaxBookedOffers, setIsMaxBookedOffers] = useState<boolean>(false);
    const [isMaxConfirmedJobs, setIsMaxConfirmedJobs] = useState<boolean>(false);
    const [isOfferConfirmationOpen, setIsOfferConfirmationOpen] = useState<boolean>(false);
    const [submittedOfferData, setSubmittedOfferData] = useState<OfferSubmitData | null>(null);
    const [showLanguageModal, setShowLanguageModal] = useState<boolean>(false);
    const [pendingRedirect, setPendingRedirect] = useState<boolean>(false);
    const [isOtherShiftsPromptOpen, setIsOtherShiftsPromptOpen] = useState<boolean>(false);
    const [checkingOtherShifts, setCheckingOtherShifts] = useState<boolean>(false);
    const [otherShiftsCount, setOtherShiftsCount] = useState<number>(0);
    const [isWorkExpModalOpen, setIsWorkExpModalOpen] = useState<boolean>(false);
    const [membershipDialog, setMembershipDialog] = useState<MembershipDialogCopy | null>(null);
    const [membershipSnapshot, setMembershipSnapshot] = useState<{
        actorId: number; refresh: number; membership: MembershipStatusResponse;
    } | null>(null);
    
    const { checkIfLanguagesNeeded } = useLanguageCheck();

    const locationState = useMemo(
        () => history.location.state as any,
        [history.location.state]
    );
    const { jobId: jobIdFromState, id, job: jobData, currentFilters, officeScope: officeScopeFromState } = locationState || {};
    const jobId = jobIdFromState || id || jobData?.id;
    const [hygienist, setHygienist] = usePersistedState<HygienistProps>({
        entityName: entities.hygienist
    });
    const jobIdentity = `${Number(hygienist?.id)}:${Number(jobId)}`;
    const jobRequestKey = `${jobIdentity}:${eligibilityRefresh}`;
    const [fetchedJob, setFetchedJob] = useState<{ identity: string; key: string; job: JobProps } | null>(null);
    // Keep this shift's display and dialogs mounted while eligibility refreshes.
    // A response for another actor/shift is never reused.
    const job: JobProps = fetchedJob?.identity === jobIdentity ? fetchedJob.job : (jobData || ({} as JobProps));
    const [getJobDetailsLoading, setGetJobDetailsLoading] = useState(false);
    const [rateBenchmark, setRateBenchmark] = useState<RateBenchmarkResponse | null>(null);
    
    // Extract and memoize officeScope for navigation
    const officeScope: { locationId: number; officeName?: string; sourceJobId?: number } | null = useMemo(() => {
        if (officeScopeFromState?.locationId) {
            return officeScopeFromState;
        }
        return null;
    }, [officeScopeFromState]);
    
    const isFavouritedByOffice = useOfficeFavourite(hygienist?.id, job?.locationId, eligibilityRefresh);
    const membershipStatus = membershipSnapshot?.actorId === Number(hygienist?.id)
        && membershipSnapshot.refresh === eligibilityRefresh ? membershipSnapshot.membership : null;

    // Refs to prevent infinite loops and track fetch state
    const hasFetchedInitialData = useRef(false);
    const hygienistRef = useRef(hygienist);
    
    // Keep hygienist ref updated
    useEffect(() => {
        hygienistRef.current = hygienist;
    }, [hygienist]);

    // ===== API CALLS =====
    // Stable callbacks using refs to avoid dependency issues
    const handleHideOfferSuccess = useCallback(() => {
        setIsOpenHideOfferModal(false);
        requestHygienistCalendarRefresh();
        history.goBack();
    }, [history]);

    const [hygienistOffers, setHygienistOffers] = useState<PartialOffer[]>([]);

    const handleOffersSuccess = useCallback((offers: PartialOffer[]) => {
        setHygienistOffers(offers || []);
        const postedOffers = (offers || []).filter(offer => offer.status === PARTIAL_OFFER_STATUS.POSTED);
        const maxBooked = hygienistRef.current?.maxBooked ?? 0;
        setIsMaxBookedOffers(postedOffers.length >= maxBooked * 2);
    }, []);

    const handleJobsSuccess = useCallback((jobs: JobProps[]) => {
        const confirmedJobs = jobs.filter(j => j.status === JOB_STATUSES.CONFIRMED);
        const maxBooked = hygienistRef.current?.maxBooked ?? 0;
        setIsMaxConfirmedJobs(confirmedJobs.length >= maxBooked);
    }, []);

    const [makePartialOfferLoading, setMakePartialOfferLoading] = useState<boolean>(false);

    const [hideOfferLoading, , hideOffer] = useActionApi({
        apiMethod: Api.hideOffer,
        onSuccess: handleHideOfferSuccess
    });

    const [getHygienistOffersLoading, , getHygienistOffers] = useActionApi({
        apiMethod: Api.getHygienistOffers,
        onSuccess: handleOffersSuccess,
        onError: useCallback(() => {}, [])
    });

    const [getHygienistJobsLoading, , getHygienistJobs] = useActionApi({
        apiMethod: Api.getHygienistJobs,
        onSuccess: handleJobsSuccess,
        onError: useCallback(() => {}, [])
    });

    useEffect(() => {
        let isCancelled = false;

        const fetchRateBenchmark = async () => {
            if (_.isEmpty(job)) {
                setRateBenchmark(null);
                return;
            }

            const benchmarkParams = buildRateBenchmarkRequestParamsFromJob(job);
            if (!benchmarkParams) {
                setRateBenchmark(null);
                return;
            }

            try {
                const benchmark = await Api.getRateBenchmarks(benchmarkParams);
                if (!isCancelled) {
                    setRateBenchmark(benchmark);
                }
            } catch {
                if (!isCancelled) {
                    setRateBenchmark(null);
                }
            }
        };

        fetchRateBenchmark();

        return () => {
            isCancelled = true;
        };
    }, [job]);

    useEffect(() => {
        let isCancelled = false;
        setMembershipSnapshot(null);

        const fetchMembershipStatus = async () => {
            try {
                const membership = await Api.getMembershipStatus();
                if (!isCancelled) {
                    setMembershipSnapshot({ actorId: Number(hygienist?.id), refresh: eligibilityRefresh, membership });
                }
            } catch {
                if (!isCancelled) {
                    setMembershipSnapshot(null);
                }
            }
        };

        fetchMembershipStatus();
        return () => {
            isCancelled = true;
        };
    }, [hygienist?.id, eligibilityRefresh]);

    // ===== COMPUTED VALUES =====
    const shouldFetchJob = useMemo(() => _.isEmpty(jobData) && jobId, [jobData, jobId]);
    const isJobEmpty = useMemo(() => _.isEmpty(job), [job]);
    const hasValidShifts = useMemo(() => job?.shifts && job.shifts.length > 0, [job?.shifts]);
    const requesterMembershipDecision = useMemo(
        () => membershipStatus ?? getJobRequesterMembershipDecision(job),
        [job, membershipStatus]
    );

    // T2-6567: a talent only "qualifies" to Instabook a shift when the office enabled
    // Instabook AND the talent's membership tier allows it. Used to decide whether
    // Send Offer can be safely hidden in favour of the Instabook CTA.
    const isTalentQualifiedForInstabook = useMemo(() => {
        const hasInstabook = Number(job?.Instabook) === 1
            && (eligibilityRefresh === 0 || fetchedJob?.key === jobRequestKey);
        const isQualified = canUseInstabookByMembership({
            membership: membershipStatus,
            isFavourite: isFavouritedByOffice,
        });
        return hasInstabook && isQualified;
    }, [job, membershipStatus, isFavouritedByOffice, eligibilityRefresh, fetchedJob, jobRequestKey]);

    /** Hide Send Offer when this talent is already on the job (e.g. InstaBook), has an active partial offer, or is an InstaBook candidate who can actually Instabook */
    const canShowSendOffer = useMemo(() => {
        return canShowAvailableJobSendOffer({
            hasValidShifts,
            job,
            hygienistId: hygienist?.id,
            hygienistOffers,
            isTalentQualifiedForInstabook,
        });
    }, [hasValidShifts, job?.hygienistId, job?.id, hygienist?.id, hygienistOffers, job, isTalentQualifiedForInstabook]);

    const offerActionStatus = useMemo(() => {
        if (!hasValidShifts || canShowSendOffer) return null;
        /** T2-5797 / T2-6567: suppress the "application submitted" banner only for an
         * Instabook shift the talent is a candidate for AND can actually Instabook
         * (the Instabook CTA renders there). On non-Instabook shifts, or when the
         * talent can't Instabook, fall through so the correct banner shows. */
        if ((job as any).isInstabookCandidate && (job as any).Instabook && isTalentQualifiedForInstabook) {
            return null;
        }
        const myId = Number(hygienist?.id);
        const jobHyg = Number(job.hygienistId ?? 0);
        if (jobHyg > 0 && jobHyg !== myId) {
            return { text: 'This shift is already assigned.', variant: 'caution' as const };
        }
        if (jobHyg === myId && job.Autobook === 1) {
            return { text: "You're confirmed on this Auto-book shift.", variant: 'success' as const };
        }
        if (jobHyg === myId) {
            return { text: "You're already on this shift.", variant: 'success' as const };
        }
        return { text: "You've already submitted an offer for this shift.", variant: 'success' as const };
    }, [hasValidShifts, canShowSendOffer, hygienist?.id, job.hygienistId, job.Autobook, job, isTalentQualifiedForInstabook]);

    const formattedPostedDate = useMemo(() => {
        return job.postedOn ? dayjs(job.postedOn).format('dddd, MMM D') : '';
    }, [job.postedOn]);

    const formattedShiftDate = useMemo(() => {
        return job.shifts?.[0]?.shiftDate ? dayjs(job.shifts[0].shiftDate).format('dddd, MMM D') : '';
    }, [job.shifts]);

    const shiftTime = useMemo(() => {
        if (!job.shifts?.[0]) return '';
        return `${job.shifts[0].postedStart} - ${job.shifts[0].postedEnd}`;
    }, [job.shifts]);

    const avgRecall = useMemo(() => job.location?.avgApptTime || '30 min', [job.location?.avgApptTime]);

    const proposedRate = useMemo(() => {
        return job.proposedRate ?? job.avgRate ?? job.hourlyRate;
    }, [job.proposedRate, job.avgRate, job.hourlyRate]);

    const rateCalculationTalent = useMemo(
        () => getRateCalculationTalent(hygienist, requesterMembershipDecision),
        [hygienist, requesterMembershipDecision]
    );

    const marketBenchmarkTopFloorRate = useMemo(
        () => getTalentMarketBenchmarkRate(rateBenchmark),
        [rateBenchmark]
    );

    const rateRange = useMemo(() => {
        const rate = proposedRate;
        if (!rate) return '';
        const range = calculateRateRange(
            rate,
            rateCalculationTalent.graduationYear,
            rateCalculationTalent.status,
            undefined,
            marketBenchmarkTopFloorRate
        );
        return `$${range.bottomRate} - $${range.topRate}`;
    }, [proposedRate, rateCalculationTalent, marketBenchmarkTopFloorRate]);

    const paymentDescription = useMemo(() => {
        const terms = getPaymentTerms(job.paymentTerms);
        const method = job.paymentMethod
            ? `typically via ${getPaymentMethod(job.paymentMethod, job.location?.country)}`
            : '';
        return `Office Pays: ${terms} ${method}`;
    }, [job.paymentTerms, job.paymentMethod, job.location?.country]);

    const safetyGuidelinesText = useMemo(() => {
        return `This office has confirmed they meet or exceed the ${job.location?.province || ''} guidelines for workplace safety and infection control.`;
    }, [job.location?.province]);

    const hasEndorsements = useMemo(() => {
        return !!job.location?.ppeEndorsed || !!job.location?.friendlyEndorsed || !!job.location?.equipEndorsed;
    }, [job.location]);

    // Partial offer logic
    const canShowPartialOffer = useMemo(() => {
        if (!hasValidShifts) return false;
        
        const start: Date = stringToDate(job.shifts[0].postedStart);
        const end: Date = stringToDate(job.shifts[0].postedEnd);
        const diffMs = end.getTime() - start.getTime();
        const hoursToMs = (hours: number) => hours * 60 * 60 * 1000;
        const isPartialOfferPossible = diffMs >= hoursToMs(MINIMUM_PARTIAL_SHIFT_HOURS);

        const jobStartDateFormatted = `${job.startDate?.trim() || ''} ${job.shifts[0].postedStart?.trim() || ''}`;
        
        const createSafariCompatibleDate = (dateTimeStr: string) => {
            if (!dateTimeStr || !dateTimeStr.includes(' ')) return new Date();
            const [datePart, timePart] = dateTimeStr.split(' ');
            if (!datePart || !timePart) return new Date();
            const [year, month, day] = datePart.split('-').map(Number);
            const [hours, minutes] = timePart.split(':').map(Number);
            return new Date(year, month - 1, day, hours, minutes);
        };

        const localJobStartDate = createSafariCompatibleDate(jobStartDateFormatted);
        const jobStartDate = moment(localJobStartDate);
        const now = moment();
        const hoursUntilShift = jobStartDate.diff(now, 'hours', true);
        const isSameDay = jobStartDate.isSame(now, 'day');
        const isWithinTimeWindow = (!isNaN(hoursUntilShift) && hoursUntilShift <= PARTIAL_OFFER_TIME_WINDOW) || isSameDay;

        const allowPartialOffersRaw = (job as any).allowPartialOffers ?? (job as any).partialOffersAllowed ?? true;
        const isPartialOfferAllowed = allowPartialOffersRaw === true || allowPartialOffersRaw === 1;

        return Boolean(isPartialOfferPossible && isWithinTimeWindow && isPartialOfferAllowed);
    }, [hasValidShifts, job]);

    // Track data for analytics
    const memoizedTrackData = useMemo(() => {
        if (_.isEmpty(job)) return null;
        return {
            isInstabookEnabled: !!job.Instabook,
            recommendedHourlyRate: job.proposedRate,
            currencyCode: job.location?.country ? 'USD' : 'CAD',
            dentalOfficeLocationId: job.locationId,
            dentalOfficeId: job.dentistId,
            dentalOfficeStatus: convertDentalOfficeStatus(job.location?.status ?? 0),
            shiftStartDate: hasValidShifts ? new Date(
                job.startDate?.replace(/-/g, '/') + ' ' + job.shifts[0].postedStart
            ).toISOString() : '',
            shiftEndDate: hasValidShifts ? new Date(
                job.startDate?.replace(/-/g, '/') + ' ' + job.shifts[0].postedEnd
            ).toISOString() : '',
            shiftId: job.shifts?.[0]?.id,
            shiftPostDate: job.postedOn ? new Date(job.postedOn).toISOString() : ''
        };
    }, [job, hasValidShifts]);

    // ===== EFFECTS =====
    // Pin detail responses to their route and ignore requests that complete
    // after the Talent has opened another shift.
    useEffect(() => {
        let cancelled = false;
        if (!jobId || (!shouldFetchJob && eligibilityRefresh === 0)) {
            setGetJobDetailsLoading(false);
            return;
        }
        setGetJobDetailsLoading(true);
        Api.getJobDetails(jobId).then(data => {
            if (!cancelled && Number(data?.id) === Number(jobId)) setFetchedJob({ identity: jobIdentity, key: jobRequestKey, job: data });
        }).catch(() => {}).finally(() => { if (!cancelled) setGetJobDetailsLoading(false); });
        return () => { cancelled = true; };
    }, [shouldFetchJob, jobId, eligibilityRefresh, jobIdentity, jobRequestKey]);

    // The heart is Talent -> office. It has no bearing on booking eligibility.
    useEffect(() => {
        let cancelled = false;
        setFavouriteDentist(false);
        if (!job?.locationId) {
            setCheckFavouriteLocationLoading(false);
            return;
        }
        setCheckFavouriteLocationLoading(true);
        Api.checkFavouriteLocation(job.locationId).then(data => {
            if (!cancelled) setFavouriteDentist(Number(data?.isFavouriteDentist) === 1);
        }).catch(() => {}).finally(() => { if (!cancelled) setCheckFavouriteLocationLoading(false); });
        return () => { cancelled = true; };
    }, [job?.locationId, hygienist?.id]);

    // Fetch hire jobs count on mount (only once)
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const res = await Api.getAvailableHireJobsCount();
                if (mounted) setHireJobsCount(Number(res?.result ?? 0));
            } catch {}
        })();
        return () => { mounted = false; };
    }, []);

    // Refresh hygienist data on mount (only once)
    useEffect(() => {
        const refreshHygienistData = async () => {
            try {
                const ts = (window as any).TempStars;
                if (!ts) return;
                const userAccount = ts.User.getCurrentUser();
                if (userAccount?.hygienistId && typeof ts.Hygienist?.refresh === 'function') {
                    await ts.Hygienist.refresh();
                }
            } catch {}
        };
        refreshHygienistData();
    }, []);

    // Update track data when job changes
    useEffect(() => {
        setTrackData(memoizedTrackData);
    }, [memoizedTrackData]);

    // Fetch max booked data on mount only (with ref guard to prevent re-fetching)
    useEffect(() => {
        if (hasFetchedInitialData.current) return;
        hasFetchedInitialData.current = true;
        
        getHygienistJobs('');
        getHygienistOffers({});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Keep completed-shift evidence in sync for the maximum-booking CTA.
    useEffect(() => {
        setCompletedShifts(hygienist?.completedJobs ?? 0);
    }, [hygienist?.completedJobs]);

    // ===== HANDLERS =====
    const handleBackNavigation = useCallback(() => {
        const state = history.location.state as any;
        // T2-7022: list-originated details now prepare their previous entry
        // with the restoration state. A real POP keeps MemoryRouter and the
        // browser history bridge aligned and prevents a synthetic list push
        // from sending browser Forward back to the details screen unexpectedly.
        if (state?.listKey) {
            history.goBack();
            return;
        }
        if (officeScope?.locationId) {
            history.push('availableJobs', { 
                officeScope,
                locationId: officeScope.locationId,
                officeName: officeScope.officeName,
                sourceJobId: officeScope.sourceJobId,
                preserveFilters: true,
                restoreListState: true,
                listKey: state?.listKey,
                ...currentFilters,
            });
            return;
        }
        const date: string | undefined = state?.filter?.date;
        const isDateFiltered: boolean = state?.isDateFiltered;
        const listKeyFromState: string | undefined = state?.listKey;
        const dateProps = isDateFiltered ? { date, withDate: true } : {};
        history.push('availableJobs', { 
            preserveFilters: true,
            restoreListState: true,
            listKey: listKeyFromState,
            ...currentFilters,
            ...dateProps,
        });
    }, [currentFilters, history, officeScope]);

    const handleViewMap = () => {
        const hygienistAddress = hygienist?.lat + ',+' + hygienist?.lon;
        const officeAddress = job.location?.lat + ',+' + job.location?.lon;
        const url = `https://www.google.com/maps/dir/${hygienistAddress}/${officeAddress}`;
        if ((window as any).Cordova) {
            (window as any).cordova.InAppBrowser.open(url, '_system', 'location=yes');
        } else {
            window.open(url, '_blank', 'location=yes');
        }
    };

    const handleJobBoardClick = () => {
        (window as any).TempStars?.App?.goReact?.('jobPostsList');
    };

    const fetchLatestHygienistData = async () => {
        try {
            setIsLoadingHygienist(true);
            const user = await Api.getUser();
            if (user?.hygienist) {
                setHygienist(user.hygienist);
                try {
                    if (TempStars?.Hygienist?.refresh) {
                        await TempStars.Hygienist.refresh();
                    }
                } catch {}
            }
        } catch {}
        finally {
            setIsLoadingHygienist(false);
        }
    };

    const sendOfferHandler = async () => {
        if (!hasValidShifts || !canShowSendOffer) return;
        
        await fetchLatestHygienistData();
        
        if (isMaxBookedOffers || isMaxConfirmedJobs) {
            setIsMaxBookedPopupOpen(true);
            return;
        }

        try {
            const workExp = await Api.getWorkExperience();
            if (!workExp || workExp.length === 0) {
                setIsWorkExpModalOpen(true);
                return;
            }
        } catch {
            // Don't block the flow if the check itself fails
        }
        
        if (job.location?.province === 'Ontario' && 
            hygienist?.skills?.find(skill => skill.name === 'CDA2') && 
            !hygienist?.CDHONumber) {
            setIsOpenCDHOCertificateModal(true);
        } else {
            setOfferType('full');
            setIsOfferModalOpen(true);
        }
    };

    /** Navigate to the main home screen */
    const goHome = useCallback(() => {
        (window as any).TempStars?.App?.goReact('hygienistHome');
    }, []);

    /** Navigate to the office-scoped available shifts list */
    const goToOfficeScopedList = useCallback(() => {
        const normalizedLocationId = job.locationId || job.location?.id;
        if (!normalizedLocationId) {
            goHome();
            return;
        }
        history.push('availableJobs', {
            officeScope: {
                locationId: normalizedLocationId,
                officeName: job.location?.practiceName,
                sourceJobId: job.id,
            },
            locationId: normalizedLocationId,
            officeName: job.location?.practiceName,
            sourceJobId: job.id,
        });
    }, [goHome, history, job.id, job.location?.id, job.location?.practiceName, job.locationId]);

    /** Check if the same office has other available shifts */
    const checkOtherShiftsForOffice = useCallback(async (): Promise<boolean> => {
        const normalizedLocationId = job.locationId || job.location?.id;
        if (!normalizedLocationId) {
            return false;
        }
        try {
            setCheckingOtherShifts(true);
            const shifts: JobProps[] = await Api.getAvailableJobs(job.type || 0, undefined, normalizedLocationId);
            const others = (shifts || []).filter((shift: JobProps) => shift.id !== job.id);
            setOtherShiftsCount(others.length);
            return others.length > 0;
        } catch {
            return false;
        } finally {
            setCheckingOtherShifts(false);
        }
    }, [job.id, job.location?.id, job.locationId, job.type]);

    /** Post-offer flow: check for other shifts and navigate accordingly */
    const continuePostOfferFlow = useCallback(async () => {
        await runPostOfferFlow({
            officeScopeLocationId: officeScope?.locationId,
            goToOfficeScopedList,
            checkOtherShiftsForOffice,
            showOtherShiftsPrompt: () => setIsOtherShiftsPromptOpen(true),
            goHome,
        });
    }, [checkOtherShiftsForOffice, goHome, goToOfficeScopedList, officeScope?.locationId]);

    const handleOfferSubmit = async (data: OfferSubmitData) => {
        setIsOfferModalOpen(false);
        const statusToUse = data.showJobSeekingStatus !== undefined ? data.showJobSeekingStatus : showJobSeekingStatus;

        try {
            setMakePartialOfferLoading(true);
            await Api.makePartialOffer({
                hourlyRate: data.hourlyRate,
                jobId: job.id,
                startTime: data.startTime,
                endTime: data.endTime,
                isPartial: data.isPartial,
                showJobSeekingStatus: statusToUse ? 1 : 0
            });
        } catch (err: any) {
            setMakePartialOfferLoading(false);
            const errorState = getOfferSubmitErrorState(err);
            if (errorState.membershipDialog) {
                setMembershipDialog(errorState.membershipDialog);
                return;
            }
            if (errorState.workExperienceRequired) {
                setIsWorkExpModalOpen(true);
            }
            return;
        }
        setMakePartialOfferLoading(false);

        setSubmittedOfferData({ ...data, showJobSeekingStatus: statusToUse });
        setIsOfferConfirmationOpen(true);
    };

    const handleOfferConfirmation = async () => {
        setIsOfferConfirmationOpen(false);
        if (!submittedOfferData) return;

        try {
            const needsLanguages = await checkIfLanguagesNeeded();
            if (needsLanguages) {
                setShowLanguageModal(true);
                setPendingRedirect(true);
                TempStars?.Analytics?.track('send offer', {
                    ...trackData,
                    requestedHourlyRate: submittedOfferData.hourlyRate,
                    startTime: submittedOfferData.startTime,
                    endTime: submittedOfferData.endTime,
                    showJobSeekingStatus: submittedOfferData.showJobSeekingStatus,
                    membership_status_code: requesterMembershipDecision?.currentStatusCode ?? null,
                    membership_rate_tier: rateCalculationTalent.status,
                });
                return;
            }
        } catch {}

        TempStars?.Analytics?.track('send offer', {
            ...trackData,
            requestedHourlyRate: submittedOfferData.hourlyRate,
            startTime: submittedOfferData.startTime,
            endTime: submittedOfferData.endTime,
            showJobSeekingStatus: submittedOfferData.showJobSeekingStatus,
            membership_status_code: requesterMembershipDecision?.currentStatusCode ?? null,
            membership_rate_tier: rateCalculationTalent.status,
        });
        await continuePostOfferFlow();
    };

    const handleLanguageSubmissionSuccess = async () => {
        setShowLanguageModal(false);
        try {
            const updatedUser = await Api.getUser();
            if (updatedUser?.hygienist) {
                setHygienist(updatedUser.hygienist);
            }
        } catch {}
        if (pendingRedirect) {
            await continuePostOfferFlow();
            setPendingRedirect(false);
        }
    };

    const handleLanguageModalClose = () => {
        setShowLanguageModal(false);
        if (pendingRedirect) {
            goHome();
            setPendingRedirect(false);
        }
    };

    const onHideOffer = async () => {
        hideOffer({ jobId: job.id });
    };

    // ===== LOADING STATE =====
    const isLoading = getJobDetailsLoading || checkFavouriteLocationLoading ||
        isLoadingHygienist || makePartialOfferLoading || hideOfferLoading ||
        getHygienistOffersLoading || getHygienistJobsLoading;

    // Show loading state if we're fetching job details or if job is empty and we have a jobId to fetch
    if (isJobEmpty && (getJobDetailsLoading || (jobId && shouldFetchJob))) {
        return (
            <div className={styles.pageContainer}>
                <FixedLoader loading={true} />
            </div>
        );
    }

    // If job is empty and we don't have a jobId or jobData, show error state
    if (isJobEmpty && !jobId && !jobData) {
        return (
            <div className={styles.pageContainer}>
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                    <p>Unable to load job details. Please try again.</p>
                    <button onClick={handleBackNavigation} style={{ marginTop: '1rem', padding: '0.5rem 1rem' }}>
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.pageContainer}>
            {/* ===== MODALS ===== */}
            <Popup opened={isOpenCDHOCertificateModal} onClose={() => setIsOpenCDHOCertificateModal(false)}>
                <UpdateCDHOCertificate
                    onClose={() => setIsOpenCDHOCertificateModal(false)}
                    hygienist={hygienist}
                />
            </Popup>

            {isOfferModalOpen && hasValidShifts && canShowSendOffer && (
                <OfferModal
                    open={isOfferModalOpen}
                    onClose={() => setIsOfferModalOpen(false)}
                    onSubmit={handleOfferSubmit}
                    offerType={offerType}
                    job={job}
                    hygienist={hygienist}
                    membershipRateTier={rateCalculationTalent.status}
                    showJobSeekingStatus={showJobSeekingStatus}
                    setShowJobSeekingStatus={setShowJobSeekingStatus}
                    partialOfferAvailable={canShowPartialOffer}
                rateBenchmark={rateBenchmark}
                />
            )}

            <Popup opened={isOpenHideOfferModal} onClose={() => setIsOpenHideOfferModal(false)}>
                {hasValidShifts && (
                    <ShiftPopup
                        title="Hide Shift?"
                        submit="Hide Shift"
                        close="Cancel"
                        job={job}
                        onClose={() => setIsOpenHideOfferModal(false)}
                        onSubmit={onHideOffer}
                    />
                )}
            </Popup>

            {isOfferConfirmationOpen && submittedOfferData && (
                <Popup opened={isOfferConfirmationOpen} onClose={() => {}}>
                    <OfferConfirmation
                        job={job}
                        offerData={submittedOfferData}
                        hygienist={hygienist}
                        onConfirm={handleOfferConfirmation}
                    />
                </Popup>
            )}

            <MaxBookedPopup
                isOpen={isMaxBookedPopupOpen}
                onClose={() => setIsMaxBookedPopupOpen(false)}
                canRequestIncrease={canRequestMaxBookedIncrease(requesterMembershipDecision, completedShifts)}
            />

            <LanguageSelectionModal
                open={showLanguageModal}
                onClose={handleLanguageModalClose}
                onSuccess={handleLanguageSubmissionSuccess}
            />

            <ConfirmDialog
                open={isWorkExpModalOpen}
                onClose={() => setIsWorkExpModalOpen(false)}
                title={`Heads up, ${hygienist?.firstName}!`}
                content={<div>Offices need to see Work Experience in offers. It looks like you haven&apos;t added this yet.</div>}
                actions={[
                    { label: 'Fill Out Work Experience', variant: 'contained', onClick: () => {
                        setIsWorkExpModalOpen(false);
                        (window as any).TempStars?.App?.goReact('hygienistProfile', { isOpenPositionModal: true });
                    }},
                    { label: "I'll do this later", onClick: () => setIsWorkExpModalOpen(false) }
                ]}
            />

            <MembershipRestrictionModal
                isOpen={!!membershipDialog}
                copy={membershipDialog}
                onClose={() => setMembershipDialog(null)}
            />

            {/* Other Shifts Prompt */}
            <Popup opened={isOtherShiftsPromptOpen} onClose={() => setIsOtherShiftsPromptOpen(false)}>
                <div className={componentStyles.popupContainer}>
                    <div className={componentStyles.titleContainer}>
                        <h2 className={componentStyles.popupTitle}>
                            This office has other shifts posted currently, would you like to see them?
                        </h2>
                    </div>
                    <p className={componentStyles.modalDescription}>
                        {otherShiftsCount > 0
                            ? `${otherShiftsCount} other shift${otherShiftsCount === 1 ? '' : 's'} available for this office.`
                            : 'View other available shifts for this office.'}
                    </p>
                    <ContainedButton
                        className={componentStyles.modalButton}
                        variant="contained"
                        size="large"
                        onClick={() => { setIsOtherShiftsPromptOpen(false); goToOfficeScopedList(); }}
                    >
                        Yes, show me shifts
                    </ContainedButton>
                    <OutlinedButton
                        className={componentStyles.modalButton}
                        variant="outlined"
                        size="large"
                        onClick={() => { setIsOtherShiftsPromptOpen(false); goHome(); }}
                    >
                        No, thanks
                    </OutlinedButton>
                </div>
            </Popup>

            <TalentNavbar
                firstName={hygienist?.firstName}
                lastName={hygienist?.lastName}
                photoUrl={hygienist?.photoUrl || undefined}
                isHygOrAsst={hygienist?.type === 0 || hygienist?.type === 1}
                instabookActivated={hygienist?.InstabookActivated === 1}
                isTestAccount={(hygienist as any)?.isTest === 1}
                showAddShiftButton={true}
                showJobBoardButton={true}
                hireJobsCount={hireJobsCount}
                onJobBoardClick={handleJobBoardClick}
                hideAddShiftOnMobile
                pageTitle="Shift Details"
            />

            {/* ===== MAIN CONTENT ===== */}
            {!isJobEmpty && (
                <main className={styles.mainContent}>
                    <TalentPageTitle title="Shift Details" />
                    {/* ===== SUBHEADER ===== */}
                    <div className={styles.subheader}>
                        <button className={styles.backButton} onClick={handleBackNavigation}>
                            <ArrowBackIcon />
                        </button>
                        <div className={styles.subheaderActions}>
                            <button className={styles.locationButton} onClick={handleViewMap}>
                                <RoomOutlinedIcon />
                                Location
                            </button>
                            <div className={styles.heartButton}>
                                <img
                                    src={isFavouriteDentist ? heartFilled : heartUnfilled}
                                    alt={isFavouriteDentist ? 'Favourite office' : 'Not a favourite office'}
                                />
                            </div>
                            {/* T2-5762: Send Offer moved into the Offer Rate card below; subheader now shows status hints only */}
                            {hasValidShifts && !canShowSendOffer && offerActionStatus && (
                                <span
                                    className={
                                        offerActionStatus.variant === 'caution'
                                            ? styles.offerStatusHintCaution
                                            : styles.offerStatusHint
                                    }
                                    role="status"
                                >
                                    {offerActionStatus.text}
                                </span>
                            )}
                        </div>
                    </div>
                    {/* Office Card */}
                    <div className={styles.officeCard}>
                        <h1 className={styles.officeName}>{job.location?.practiceName}</h1>
                        <p className={styles.officeLocation}>
                            {job.location?.city}, {job.location?.province} {job.location?.postalCode}
                        </p>
                    </div>

                    {/* Divider above Shift Info */}
                    <hr className={styles.divider} />

                    {/* Shift Info Row - separate boxes */}
                    <div className={styles.shiftInfoRow}>
                        <div className={styles.shiftInfoItem}>
                            <div className={styles.shiftInfoLabel}>
                                <div className={styles.shiftInfoIconCircle}>
                                    <CalendarTodayOutlinedIcon />
                                </div>
                                Shift Date
                            </div>
                            <div className={styles.shiftInfoValue}>{formattedShiftDate}</div>
                        </div>
                        <div className={styles.shiftInfoItem}>
                            <div className={styles.shiftInfoLabel}>
                                <div className={styles.shiftInfoIconCircle}>
                                    <AccessTimeOutlinedIcon />
                                </div>
                                Shift Time
                            </div>
                            <div className={styles.shiftInfoValue}>{shiftTime}</div>
                        </div>
                        {job.shifts?.[0]?.firstPatientTime && (
                            <div
                                className={styles.shiftInfoItem}
                                id="available-job-first-patient-time"
                                data-testid="available-job-first-patient-time"
                            >
                                <div className={styles.shiftInfoLabel}>
                                    <div className={styles.shiftInfoIconCircle}>
                                        <AccessTimeOutlinedIcon />
                                    </div>
                                    First patient time
                                </div>
                                <div className={styles.shiftInfoValue}>
                                    {job.shifts[0].firstPatientTime}
                                </div>
                            </div>
                        )}
                        <div className={styles.shiftInfoItem}>
                            <div className={styles.shiftInfoLabel}>
                                <div className={styles.shiftInfoIconCircle}>
                                    <TimelapseOutlinedIcon />
                                </div>
                                Avg. Recall
                            </div>
                            <div className={styles.shiftInfoValue}>{avgRecall}</div>
                        </div>
                    </div>

                    {/* Posted On */}
                    <div className={styles.postedOn}>Posted On: {formattedPostedDate}</div>

                    {/* Divider below Posted On */}
                    <hr className={styles.divider} />

                    {/* Instabook Banner */}
                    {isTalentQualifiedForInstabook && (
                        <div className={styles.instabookBanner}>
                            <img src={instabook} alt="Instabook" className={styles.instabookIcon} />
                            <div className={styles.instabookContent}>
                                <div className={styles.instabookTitle}>Instabook available</div>
                                <div className={styles.instabookDescription}>
                                    Skip the line! You can instantly book this shift at the Proposed Rate, without waiting for your offer to be accepted.
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Offer Rate Card — T2-5762: title + rate row 1, payment description + submit-offer CTA share row 2 */}
                    <div className={styles.offerRateCard}>
                        <div className={styles.offerRateRow}>
                            <div className={styles.offerRateTitle}>
                                <AttachMoneyOutlinedIcon />
                                Your Offer Rate
                            </div>
                            <div className={styles.offerRateValue}>{rateRange}</div>
                        </div>
                        <div className={styles.offerRateRow}>
                            <div className={styles.offerRateContent}>
                                <div className={styles.offerRateDescription}>{paymentDescription}</div>
                                {job.location?.country === COUNTRY.US && typeof job.paysW2 === 'number' && (
                                    <div className={styles.offerRateMetaText}>
                                        Working relationship: {getPaymentType(job.paysW2)}
                                    </div>
                                )}
                            </div>
                            {hasValidShifts && canShowSendOffer && (
                                <button
                                    type="button"
                                    className={styles.submitOfferAtRateButton}
                                    onClick={sendOfferHandler}
                                >
                                    Submit offer at your rate
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Details Grid */}
                    <div className={styles.detailsGrid}>
                        <div className={styles.detailItem}>
                            <div className={styles.detailIcon}><PersonOutlineOutlinedIcon /></div>
                            <div className={styles.detailContent}>
                                <div className={styles.detailLabel}>Primary Contact</div>
                                <div className={styles.detailValue}>{job.location?.primaryContact || '-'}</div>
                            </div>
                        </div>
                        <div className={styles.detailItem}>
                            <div className={styles.detailIcon}><WavesOutlinedIcon /></div>
                            <div className={styles.detailContent}>
                                <div className={styles.detailLabel}>Ultrasonic</div>
                                <div className={styles.detailValue}>{job.location?.ultrasonic || '-'}</div>
                            </div>
                        </div>
                        <div className={styles.detailItem}>
                            <div className={styles.detailIcon}><DesktopMacOutlinedIcon /></div>
                            <div className={styles.detailContent}>
                                <div className={styles.detailLabel}>Software</div>
                                <div className={styles.detailValue}>{job.location?.software || '-'}</div>
                            </div>
                        </div>
                        <div className={styles.detailItem}>
                            <div className={styles.detailIcon}><TimeToLeaveOutlinedIcon /></div>
                            <div className={styles.detailContent}>
                                <div className={styles.detailLabel}>Parking</div>
                                <div className={styles.detailValue}>{getTalentParkingLabel(job.location?.parking) || '-'}</div>
                            </div>
                        </div>
                        <div className={styles.detailItem}>
                            <div className={styles.detailIcon}><TimelapseOutlinedIcon /></div>
                            <div className={styles.detailContent}>
                                <div className={styles.detailLabel}>Avg Recall</div>
                                <div className={styles.detailValue}>{job.location?.avgApptTime || '-'}</div>
                            </div>
                        </div>
                        <div className={styles.detailItem}>
                            <div className={styles.detailIcon}><DiningOutlinedIcon /></div>
                            <div className={styles.detailContent}>
                                <div className={styles.detailLabel}>Downtime</div>
                                <div className={styles.detailValue}>
                                    {job.shifts?.[0] ? formatDowntimeLabel(job.shifts[0].gapUnpaid) : '—'}
                                </div>
                            </div>
                        </div>
                        <div className={styles.detailItem}>
                            <div className={styles.detailIcon}><BlurLinearOutlinedIcon /></div>
                            <div className={styles.detailContent}>
                                <div className={styles.detailLabel}>Radiography</div>
                                <div className={styles.detailValue}>{job.location?.radiography || '-'}</div>
                            </div>
                        </div>
                        <div className={styles.detailItem}>
                            <div className={styles.detailIcon}><LineAxisOutlinedIcon /></div>
                            <div className={styles.detailContent}>
                                <div className={styles.detailLabel}>Charting</div>
                                <div className={styles.detailValue}>{job.location?.charting || '-'}</div>
                            </div>
                        </div>
                        <div className={styles.detailItem}>
                            <div className={styles.detailIcon}><SpaOutlinedIcon /></div>
                            <div className={styles.detailContent}>
                                <div className={styles.detailLabel}>Scent Policy</div>
                                <div className={`${styles.detailValue} ${job.location?.scentFreePolicy !== 'scent-free' ? styles.warning : ''}`}>
                                    {job.location?.scentFreePolicy === 'scent-free' ? 'Scent-Free' : 'Not Scent-Free'}
                                </div>
                            </div>
                        </div>
                        {(!!job.isOrtho || !!job.isRestorative) && (
                            <div className={styles.detailItem}>
                                <div className={styles.detailIcon}><MedicalServicesOutlinedIcon /></div>
                                <div className={styles.detailContent}>
                                    <div className={styles.detailLabel}>Additional Skills</div>
                                    <div className={`${styles.detailValue} ${styles.skillRequired}`}>
                                        {job.isOrtho ? 'Orthodontic' : 'Restorative'}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Endorsements Section */}
                    {hasEndorsements && (
                        <div className={styles.endorsementsSection}>
                            <div className={styles.endorsementsTitle}>
                                Your fellow TempStars members have worked there and say:
                            </div>
                            <div className={styles.endorsementsList}>
                                {job.location?.ppeEndorsed && (
                                    <div className={styles.endorsementItem}>
                                        <div className={`${styles.endorsementIcon} ${styles.ppe}`}>
                                            <SecurityOutlinedIcon />
                                        </div>
                                        <span className={styles.endorsementText}>PPE Endorsed</span>
                                    </div>
                                )}
                                {job.location?.friendlyEndorsed && (
                                    <div className={styles.endorsementItem}>
                                        <div className={`${styles.endorsementIcon} ${styles.friendly}`}>
                                            <SentimentSatisfiedAltOutlinedIcon />
                                        </div>
                                        <span className={styles.endorsementText}>Friendly team</span>
                                    </div>
                                )}
                                {job.location?.equipEndorsed && (
                                    <div className={styles.endorsementItem}>
                                        <div className={`${styles.endorsementIcon} ${styles.equipment}`}>
                                            <LaptopMacOutlinedIcon />
                                        </div>
                                        <span className={styles.endorsementText}>Good equipment</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Admin Tasks for admin jobs (T2-6509: attire is nested in adminTasks, not top-level job.attire) */}
                    {job.type === 2 && job.adminTasks && (
                        <AdminTasksDisplay
                            adminTasks={job.adminTasks}
                            attire={job.adminTasks.attire}
                        />
                    )}

                    {/* Safety Guidelines */}
                    <div className={styles.safetyGuidelines}>
                        <CheckCircleOutlineIcon />
                        <span className={styles.safetyText}>{safetyGuidelinesText}</span>
                    </div>

                    {/* Instabook CTA + Send Offer fallback */}
                    <InstaBookOfferModal
                        key={`${hygienist?.id}:${job.id}`}
                        job={job}
                        history={history}
                        trackData={trackData}
                        isFavourite={isFavouritedByOffice}
                        onEligibilityChanged={() => setEligibilityRefresh(value => value + 1)}
                        isMaxBooked={isMaxConfirmedJobs}
                        membershipRateTier={rateCalculationTalent.status}
                        membershipStatusCode={requesterMembershipDecision?.currentStatusCode ?? null}
                        canUseInstabook={isTalentQualifiedForInstabook}
                        canRequestMaxBookedIncrease={canRequestMaxBookedIncrease(requesterMembershipDecision, completedShifts)}
                    />

                    {hasValidShifts && canShowSendOffer && (
                        <button
                            type="button"
                            className={clsx(styles.hideShiftLink, 'no-fastclick')}
                            onClick={() => setIsOpenHideOfferModal(true)}
                        >
                            Hide This Shift <VisibilityOffIcon sx={{ fontSize: 18 }} />
                        </button>
                    )}
                </main>
            )}

            <FixedLoader loading={isLoading} />
        </div>
    );
};

export default AvailableJobPage;
